/**
 * Trade Pricing Cookie Management
 * 
 * This script detects trade customer status from Shopify's customer object
 * and sets a cookie that can be read by Boost Commerce templates.
 * 
 * Integration: Add this to your theme's layout/theme.liquid file
 * Place before closing </body> tag
 */

(function() {
  'use strict';

  // Configuration
  const COOKIE_NAME = 'customer_trade_status';
  const COOKIE_MAX_AGE = 86400; // 24 hours in seconds
  const COOKIE_PATH = '/';
  const COOKIE_SAME_SITE = 'Lax';

  /**
   * Set a cookie with proper attributes
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} maxAge - Max age in seconds
   */
  function setCookie(name, value, maxAge) {
    const cookieString = [
      `${name}=${encodeURIComponent(value)}`,
      `max-age=${maxAge}`,
      `path=${COOKIE_PATH}`,
      `SameSite=${COOKIE_SAME_SITE}`,
      window.location.protocol === 'https:' ? 'Secure' : ''
    ].filter(Boolean).join('; ');
    
    document.cookie = cookieString;
    console.log('[Trade Pricing] Cookie set:', name, '=', value);
  }

  /**
   * Delete a cookie
   * @param {string} name - Cookie name
   */
  function deleteCookie(name) {
    document.cookie = `${name}=; max-age=0; path=${COOKIE_PATH}`;
    console.log('[Trade Pricing] Cookie deleted:', name);
  }

  /**
   * Get cookie value
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null
   */
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return decodeURIComponent(parts.pop().split(';').shift());
    }
    return null;
  }

  /**
   * Detect customer trade status from Shopify customer object
   * @returns {Object} Trade status object
   */
  function detectTradeStatus() {
    // Check if customer is logged in
    if (typeof window.Shopify === 'undefined' || 
        typeof window.Shopify.customer === 'undefined' ||
        !window.Shopify.customer) {
      return {
        isLoggedIn: false,
        isTrade: false,
        discount: 0
      };
    }

    const customer = window.Shopify.customer;
    const customerTags = customer.tags || [];

    // Check for TRADE tag
    const hasTradTag = customerTags.includes('TRADE');
    
    if (!hasTradTag) {
      return {
        isLoggedIn: true,
        isTrade: false,
        discount: 0
      };
    }

    // Check for trade_20 tag for 20% discount
    const hasTrade20Tag = customerTags.includes('trade_20');
    const discountPercent = hasTrade20Tag ? 20 : 10;

    return {
      isLoggedIn: true,
      isTrade: true,
      discount: discountPercent
    };
  }

  /**
   * Initialize trade pricing cookie
   */
  function init() {
    const tradeStatus = detectTradeStatus();

    if (!tradeStatus.isLoggedIn) {
      // Customer not logged in - delete cookie if exists
      const existingCookie = getCookie(COOKIE_NAME);
      if (existingCookie) {
        deleteCookie(COOKIE_NAME);
      }
      console.log('[Trade Pricing] Customer not logged in - cookie cleared');
      return;
    }

    if (tradeStatus.isTrade) {
      // Trade customer - set cookie with discount percentage
      const cookieValue = JSON.stringify({
        isTrade: true,
        discount: tradeStatus.discount,
        timestamp: Date.now()
      });
      setCookie(COOKIE_NAME, cookieValue, COOKIE_MAX_AGE);
      console.log('[Trade Pricing] Trade customer detected:', tradeStatus.discount + '% discount');
    } else {
      // Regular customer - set cookie with no discount
      const cookieValue = JSON.stringify({
        isTrade: false,
        discount: 0,
        timestamp: Date.now()
      });
      setCookie(COOKIE_NAME, cookieValue, COOKIE_MAX_AGE);
      console.log('[Trade Pricing] Regular customer - no discount');
    }
  }

  /**
   * Refresh cookie on customer login/logout events
   */
  function setupEventListeners() {
    // Listen for customer account navigation
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail && event.detail.sectionId === 'header') {
        init();
      }
    });

    // Listen for customer login via AJAX
    if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
      // In theme editor, refresh on every page load
      init();
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      init();
      setupEventListeners();
    });
  } else {
    init();
    setupEventListeners();
  }

  // Expose public API for manual refresh if needed
  window.tradePricing = {
    refresh: init,
    getStatus: detectTradeStatus,
    getCookie: function() {
      const value = getCookie(COOKIE_NAME);
      return value ? JSON.parse(value) : null;
    }
  };

})();
